/* eslint-disable @typescript-eslint/no-explicit-any */
import { Table, TableBody } from '@one-thd/sui-atomic-components';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import React, { FC, useEffect, useState } from 'react';
import { ERR_GENERIC_LATER } from '../../../constants/response-error-messages';
import { GetLocalDeliveryData } from '../../../hooks/purchase-history-hooks';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { ODTableProps } from '../../../models/purchase-history/local-deliveries';
import { TSelectedRow } from '../../../models/purchase-history/table';
import { AlertBanner } from '../../shared/alert-banner/AlertBanner';
import { TableSkeleton } from '../../shared/skeletons/TableSkeleton';
import NoOrdersView from '../NoOrdersView';
import { ODTableHeader, ODTableRow } from './index';
import { ODTableHeaderValues } from './ODTableHeaderData';
import { ODTablePagination } from './ODTablePagination';

export const ODTable: FC<ODTableProps> = ({ customerType, orders }) => {
  const {
    localDeliveryData,
    localDeliveryError,
    localDeliveryLoaded,
    setLocalDeliveryInfo,
    lDRequest,
    localDeliveryAlertState,
    setLocalDeliveryAlertState,
    setOpenAddReceiptDrawer,
    dateSelectorValue,
  } = usePurchaseHistoryContext();
  const [selected, setSelected] = useState<TSelectedRow>({ 0: 0 });
  const [ldDataRendered, setLdDataRendered] = useState(false);

  const [tableHeaderState] = useState(ODTableHeaderValues());

  const data = GetLocalDeliveryData(lDRequest);

  if (
    data.localDeliveryLoaded &&
    setLocalDeliveryInfo &&
    ldDataRendered === false
  ) {
    setLocalDeliveryInfo({
      localDeliveryData: data.localDeliveryData,
      localDeliveryError: data.localDeliveryError,
      localDeliveryLoaded: data.localDeliveryLoaded,
    });
    setLdDataRendered(true);
  }

  const totalNumberOfLocalDeliveries = localDeliveryData?.orderCount ?? 0;

  useEffect(() => {
    if (localDeliveryError?.response?.status === 500) {
      setLocalDeliveryAlertState({
        alertTitle: 'Purchase History Unavailable',
        alertMessage:
          "We're sorry, there is a technical error. Please try again later.",
        status: 'error',
      });
      return;
    }
    if (localDeliveryError) {
      setLocalDeliveryAlertState({
        alertTitle: 'Purchase History Unavailable',
        alertMessage: ERR_GENERIC_LATER.message,
        status: 'error',
      });
    }
  }, [localDeliveryError]);

  if (localDeliveryError) {
    return (
      <div className="sui-col-span-1">
        {localDeliveryAlertState && (
          <AlertBanner
            headline={localDeliveryAlertState?.alertTitle}
            status={localDeliveryAlertState?.status || 'info'}
            onClose={localDeliveryAlertState?.onClose}
          >
            {localDeliveryAlertState?.alertMessage}
          </AlertBanner>
        )}
      </div>
    );
  }

  if (
    localDeliveryLoaded &&
    (totalNumberOfLocalDeliveries === 0 ||
      localDeliveryData?.orders?.length === 0)
  ) {
    return (
      <ErrorBoundary name="no-local-deliveries">
        <NoOrdersView
          customerType={customerType}
          setOpenAddReceiptModal={setOpenAddReceiptDrawer}
          activeFilter={dateSelectorValue === 'last6Months'}
        />
      </ErrorBoundary>
    );
  }

  return (
    <>
      <div className="sui-overflow-x-scroll sui-mb-5">
        <Table
          unlined
          layout="fixed"
          density="loose"
          className="sui-shadow-none sui-w-full"
        >
          <ODTableHeader tableHeaderState={tableHeaderState} />
          <TableBody>
            {/* 🚨 Will fix after merge */}
            {localDeliveryLoaded ? (
              orders?.map((order: any, index: number) => (
                <ODTableRow
                  index={index}
                  order={order}
                  selected={selected}
                  customerType={customerType}
                  setSelected={setSelected}
                  orderLength={orders.length}
                />
              ))
            ) : (
              <TableSkeleton colSpan={tableHeaderState?.length ?? 7} />
            )}
          </TableBody>
        </Table>
      </div>
      {(localDeliveryData?.orderCount || 0) > 0 && <ODTablePagination />}
    </>
  );
};
